import { canvasDocumentRuntime } from './canvasDocumentRuntime';

/**
 * Protection opaque d'un tour documentaire sur sa session logique exacte.
 *
 * Ce contrat ne connaît ni le Canvas affiché ni l'ancien verrou global : un transport
 * capture l'identité résolue au premier signal documentaire et ne peut libérer
 * que cette même résidence. Ainsi une fin tardive ne peut jamais refroidir le
 * document qu'une autre conversation a rendu visible entre-temps.
 */
export type CanvasDocumentTurnPin = Readonly<{
  /**
   * La conversation, qui EST le document épinglé (#1336).
   *
   * ⚠️ Ce contrat portait `conversationId` **et** `documentSessionId` : deux
   * champs pour un même fait depuis que la conversation est la clé du document
   * logique. Aucune époque de montage ne le complète — l'épingle protège la
   * session ENREGISTRÉE, qui survit à ses montages ; la borner à une époque
   * relâcherait le document au premier remontage, en plein tour d'agent.
   */
  conversationId: string;
  turnId: string | null;
  /**
   * La poignée d'épingle AUTHENTIQUE rendue par le runtime (#1354/D10).
   *
   * Elle autorise le tour propriétaire à réserver son propre remplacement
   * exclusif (`tryBeginExclusiveReplacement(conversationId, requestingPin)`) :
   * le runtime ne l'accepte que s'il l'a lui-même frappée, donc un appelant ne
   * peut pas se forger une exemption. Son consommateur est
   * `replaceResidentCanvasDocument` (`WebSocketPool.ts`), qui la PRÉSENTE au
   * lieu de rendre l'épingle : la rendre annulerait, depuis D9, le chargement
   * même qui demande le remplacement.
   */
  runtimePin: () => void;
  release: () => void;
}>;

export function pinCanvasDocumentTurn(input: Readonly<{
  conversationId: string;
  turnId: string | null;
  /**
   * Une connexion sans `localTurnId` doit tout de même produire un token
   * distinct : le token reste un détail de capacité, jamais une identité UI.
   */
  tokenId: string;
}>): CanvasDocumentTurnPin {
  const releaseRuntimePin = canvasDocumentRuntime.pinSession(input.conversationId, {
    kind: 'job',
    id: input.tokenId,
  });
  let released = false;

  return Object.freeze({
    conversationId: input.conversationId,
    turnId: input.turnId,
    runtimePin: releaseRuntimePin,
    release: () => {
      if (released) return;
      released = true;
      releaseRuntimePin();
    },
  });
}
